import { supabase } from "@/supabaseClient";
import { makeThumbAndDisplay } from "../imageCompress";

const BUCKET = "marketplace-media";

function sanitizeFileName(value = "") {
  return value
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-_.]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}

function buildBatchId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  );
}

export async function uploadProductImages({
  studioId,
  files,
  variants = true,
}: {
  studioId: string;
  files: (Blob | File)[];
  variants?: boolean;
}) {
  if (!studioId) {
    throw new Error("studioId is required for product image uploads.");
  }

  if (!Array.isArray(files) || files.length === 0) {
    return { urls: [] };
  }

  const batchId = buildBatchId();
  const displayUrls: string[] = [];
  const thumbUrls: string[] = [];

  for (let index = 0; index < files.length; index += 1) {
    const original = files[index];
    const rawName = original instanceof File && original.name ? original.name : `image-${index + 1}`;
    const safeNameBase = sanitizeFileName(rawName) || `image-${index + 1}`;

    const canProcess = variants && original instanceof File && original.type?.startsWith?.("image/");

    let displayFile: Blob | File = original;
    let thumbFile: Blob | File | null = null;

    if (canProcess) {
      const { thumb, display } = await makeThumbAndDisplay(original);
      displayFile = display.file;
      thumbFile = thumb.file;
    }

    const displayPath = `studios/${studioId}/products/${batchId}/${index + 1}-${safeNameBase}-display.webp`;
    const { error: displayUploadError } = await supabase.storage
      .from(BUCKET)
      .upload(displayPath, displayFile, {
        cacheControl: "31536000",
        upsert: true,
        contentType: displayFile instanceof File ? displayFile.type || undefined : undefined,
      });

    if (displayUploadError) {
      throw new Error(displayUploadError.message || "Failed to upload product image.");
    }

    const { data: displayData } = supabase.storage.from(BUCKET).getPublicUrl(displayPath);
    const displayPublicUrl = displayData?.publicUrl;
    if (!displayPublicUrl) {
      throw new Error("Could not determine product image URL.");
    }
    displayUrls.push(displayPublicUrl);

    if (thumbFile) {
      const thumbPath = `studios/${studioId}/products/${batchId}/${index + 1}-${safeNameBase}-thumb.webp`;
      const { error: thumbUploadError } = await supabase.storage
        .from(BUCKET)
        .upload(thumbPath, thumbFile, {
          cacheControl: "31536000",
          upsert: true,
          contentType: thumbFile instanceof File ? thumbFile.type || undefined : undefined,
        });

      if (thumbUploadError) {
        throw new Error(thumbUploadError.message || "Failed to upload product thumbnail.");
      }

      const { data: thumbData } = supabase.storage.from(BUCKET).getPublicUrl(thumbPath);
      const thumbPublicUrl = thumbData?.publicUrl;
      if (!thumbPublicUrl) {
        throw new Error("Could not determine product thumbnail URL.");
      }
      thumbUrls.push(thumbPublicUrl);
    } else {
      // No thumb generated (non-image or variants disabled) — fall back to display.
      thumbUrls.push(displayPublicUrl);
    }
  }

  return { urls: displayUrls, displayUrls, thumbUrls };
}
